import _ from 'lodash';
import { combineReducers } from 'redux';
import { createSelector } from 'reselect';

import { setterReducer, toggleReducer } from 'frontend/state/utils';

import types from 'frontend/state/actionTypes';

import { DIFFICULTIES } from 'shared/droptimizer';

import GameData from 'shared/gameData';
import {
  getWeaponHands,
} from 'shared/items';

/*
selectedInstance: 'BoD',
instanceOptions: [{
  instanceId: BoD,
  difficulty: 'heroic',
}, {
  instanceId: M+,
  encounterId: -1,
  difficulty: 'M10',
}, {
  instanceId: 'world bosses',
  warforge: +10,
}]
*/

const selectedInstanceReducer = (state = null, action = {}) => {
  const { type, payload } = action;

  switch (type) {
    case types.CHOOSE_DROPTIMIZER_INSTANCE:
      return payload;
    case types.LOAD_DROPTIMIZER_REPORT:
      return payload.droptimizer.instance;
    default:
      return state;
  }
};

const instanceOptionsReducer = (state = [], action = {}) => {
  const { type, payload } = action;

  switch (type) {
    case types.CHOOSE_DROPTIMIZER_INSTANCE: {
      const instanceId = payload;
      if (!_.find(state, { instanceId })) {
        // no option set exists for this instance, create empty option set
        return [
          ...state,
          {
            instanceId,
          },
        ];
      }
      // no change needed
      return state;
    }
    case types.CHOOSE_DROPTIMIZER_ENCOUNTER: {
      const { instanceId, encounterId } = payload;
      const currentOptions = _.find(state, { instanceId });
      if (currentOptions) {
        return [
          ..._.reject(state, { instanceId }),
          {
            ...currentOptions,
            encounterId,
          },
        ];
      }
      return [
        ...state,
        {
          instanceId,
          encounterId,
        },
      ];
    }
    case types.CHOOSE_DROPTIMIZER_DIFFICULTY: {
      const { instanceId, difficultyId } = payload;
      const currentOptions = _.find(state, { instanceId });
      if (currentOptions) {
        return [
          ..._.reject(state, { instanceId }),
          {
            ...currentOptions,
            difficultyId,
          },
        ];
      }
      return [
        ...state,
        {
          instanceId,
          difficultyId,
        },
      ];
    }
    case types.CHOOSE_DROPTIMIZER_WARFORGE_LEVEL: {
      const { instanceId, difficulty, warforgeLevel } = payload;
      const currentOptions = _.find(state, { instanceId });
      if (currentOptions) {
        return [
          ..._.reject(state, { instanceId }),
          {
            ...currentOptions,
            warforgeLevel: {
              ...currentOptions.warforgeLevel,
              [difficulty]: warforgeLevel,
            },
          },
        ];
      }
      return [
        ...state,
        {
          instanceId,
          warforgeLevel: {
            [difficulty]: warforgeLevel,
          },
        },
      ];
    }
    case types.CHOOSE_DROPTIMIZER_GEM: {
      const { instanceId, gemId } = payload;
      const currentOptions = _.find(state, { instanceId });
      if (currentOptions) {
        return [
          ..._.reject(state, { instanceId }),
          {
            ...currentOptions,
            gemId,
          },
        ];
      }
      return [
        ...state,
        {
          instanceId,
          gemId,
        },
      ];
    }
    case types.LOAD_DROPTIMIZER_REPORT: {
      const instanceId = payload.droptimizer.instance;
      const { droptimizer } = payload;
      const previousState = _.find(state, { instanceId }) || {};
      return [
        ..._.reject(state, { instanceId }),
        {
          instanceId,
          encounterId: droptimizer.encounter,
          difficultyId: droptimizer.difficulty,
          warforgeLevel: {
            ...previousState.warforgeLevel,
            [droptimizer.difficulty]: droptimizer.warforgeLevel,
          },
        },
      ];
    }
    default:
      return state;
  }
};

const lootSpecReducer = (state = {}, action = {}) => {
  const { type, payload } = action;

  switch (type) {
    case types.CHOOSE_DROPTIMIZER_LOOT_SPEC: {
      const { classId, specId, lootSpecId } = payload;
      return {
        ...state,
        [classId]: {
          ...state[classId],
          [specId]: lootSpecId,
        },
      };
    }
    default:
      return state;
  }
};

// Older reducers, initial version of droptimizer

// selected instance IDs
const instancesReducer = (state = [], action = {}) => {
  const { type, payload } = action;

  switch (type) {
    case types.TOGGLE_DROPTIMIZER_INSTANCE:
      return _.xor(state, [payload]);
    case types.CHOOSE_DROPTIMIZER_INSTANCE:
      return [payload];
    default:
      return state;
  }
};

const encountersReducer = (state = [], action = {}) => {
  const { type, payload } = action;

  switch (type) {
    case types.CHOOSE_DROPTIMIZER_ENCOUNTER: {
      const { encounterId } = payload;
      return [encounterId];
    }
    default:
      return state;
  }
};

const difficultyReducer = (state = 'raid-normal', action = {}) => {
  const { type, payload } = action;

  switch (type) {
    case types.CHOOSE_DROPTIMIZER_DIFFICULTY: {
      const { difficultyId } = payload;
      return difficultyId;
    }
    default:
      return state;
  }
};

const groupByReducer = setterReducer({
  set: types.CHOOSE_DROPTIMIZER_GROUP_BY,
  initial: 'slot',
});

const alternateWeaponReducer = weaponSlot => (state = null, action = {}) => {
  const { type, payload } = action;

  switch (type) {
    case types.FETCH_SIMC_ITEMS_SUCCESS: {
      // need to duplicate some sorting logic here to make sure we don't populate the default
      // with the same item in main hand and off hand
      const weapons = _.reverse(_.sortBy(_.filter(payload.bags, { slot: weaponSlot }), ['itemLevel', 'id']));
      if (_.size(weapons) > 0) {
        if (weaponSlot === 'off_hand') {
          const mainWeapons = _.reverse(
            _.sortBy(
              _.filter(
                payload.bags,
                item => {
                  const hands = getWeaponHands(item);
                  return item.slot === 'main_hand' && hands !== '2h';
                },
              ),
              ['itemLevel', 'id'],
            ),
          );
          if (_.size(mainWeapons) > 0) {
            // if first main hand weapon and off hand weapons are the same ID, use the second off hand weapon as
            // the default. this should _usually_ avoid the issue of trying to reuse 1 weapon in both slots
            if (weapons[0].id === mainWeapons[0].id) {
              return 1;
            }
          }
        }
        return 0;
      }
      return null;
    }
    case types.FETCH_SIMC_ITEMS_REQUEST:
    case types.FETCH_SIMC_ITEMS_FAILURE:
      return null;
    case types.CHOOSE_ALTERNATE_WEAPON: {
      const { slot, index } = payload;
      if (slot === weaponSlot) {
        return index;
      }
      return state;
    }
    default:
      return state;
  }
};

export default combineReducers({
  selectedInstance: selectedInstanceReducer,
  instanceOptions: instanceOptionsReducer,
  lootSpec: lootSpecReducer,
  instances: instancesReducer,
  encounters: encountersReducer,
  difficulty: difficultyReducer,
  allAzeritePowers: toggleReducer({
    toggle: types.TOGGLE_DROPTIMIZER_ALL_AZERITE_POWERS,
    initial: false,
    custom: (state, action) => {
      const { type, payload } = action;
      switch (type) {
        case types.LOAD_DROPTIMIZER_REPORT:
          return payload.droptimizer.allAzeritePowers;
        default:
          return state;
      }
    },
  }),
  mainHandAlternate: alternateWeaponReducer('main_hand'),
  offHandAlternate: alternateWeaponReducer('off_hand'),
  groupBy: groupByReducer,
  showPreviousTiers: toggleReducer({
    toggle: types.TOGGLE_DROPTIMIZER_SHOW_PREVIOUS_TIERS,
    initial: false,
  }),
});

const base = state => state.simbot.droptimizer;

const selectedInstanceSelector = state => base(state).selectedInstance;
const instancesSelector = state => base(state).instances;
const instanceOptionsSelector = state => base(state).instanceOptions;
const selectedInstanceOptionsSelector = createSelector(
  selectedInstanceSelector,
  instanceOptionsSelector,
  (instanceId, options) => _.find(options, { instanceId }) || {},
);
const selectedEncounterSelector = createSelector(
  selectedInstanceOptionsSelector,
  options => options.encounterId,
);
const selectedDifficultySelector = createSelector(
  selectedInstanceOptionsSelector,
  options => options.difficultyId || null,
);
const selectedWarforgeLevelSelector = createSelector(
  selectedInstanceOptionsSelector,
  selectedDifficultySelector,
  (options, difficulty) => {
    return _.get(options, 'warforgeLevel', {})[difficulty] || 0;
  },
);
const gemSelector = createSelector(
  selectedInstanceOptionsSelector,
  options => options.gemId || null,
);
const lootSpecSelector = state => base(state).lootSpec;
const encountersSelector = state => base(state).encounters;
const difficultySelector = state => base(state).difficulty;
const allAzeritePowersSelector = state => base(state).allAzeritePowers;
const mainHandAlternateSelector = state => base(state).mainHandAlternate;
const offHandAlternateSelector = state => base(state).offHandAlternate;
const groupBySelector = state => base(state).groupBy;
const showPreviousTiersSelector = state => base(state).showPreviousTiers;
const reportNameSelector = createSelector(
  selectedInstanceSelector,
  selectedEncounterSelector,
  selectedDifficultySelector,
  selectedWarforgeLevelSelector,
  (instanceId, encounterId, difficultyId, warforgeLevel) => {
    const difficulty = _.find(DIFFICULTIES, { id: difficultyId });
    const parts = _.filter([
      'Droptimizer',
      GameData.InstanceNames[instanceId],
      _.get(difficulty, 'name'),
      warforgeLevel ? `+${warforgeLevel} ${warforgeLevel > 10 ? 'T' : 'W'}F` : null,
      GameData.EncounterNames[encounterId],
    ]);
    return _.join(parts, ' • ');
  },
);

export const selectors = {
  selectedInstance: selectedInstanceSelector,
  selectedInstanceOptions: selectedInstanceOptionsSelector,
  selectedEncounter: selectedEncounterSelector,
  selectedDifficulty: selectedDifficultySelector,
  selectedWarforgeLevel: selectedWarforgeLevelSelector,
  gem: gemSelector,
  lootSpec: lootSpecSelector,
  instances: instancesSelector,
  encounters: encountersSelector,
  allAzeritePowers: allAzeritePowersSelector,
  difficulty: difficultySelector,
  mainHandAlternate: mainHandAlternateSelector,
  offHandAlternate: offHandAlternateSelector,
  groupBy: groupBySelector,
  showPreviousTiers: showPreviousTiersSelector,
  reportName: reportNameSelector,
};
